import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Empty, Form, Input, Modal, Select, Skeleton, Tag } from 'antd';
import type { TableColumnsType } from 'antd';
import type { CreateGlobalContractRequest, GlobalContractSummary } from '@youkuai/contracts';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Icon } from './icons.tsx';
import { useAuth } from './auth.tsx';
import { listCustomers } from './customer-api.ts';
import { createGlobalContract, linkContractProject, listGlobalContracts,
  unlinkContractProject } from './global-contract-api.ts';
import { ApiRequestError, getProject, listProjects } from './project-api.ts';
import { ModuleFullDetailDrawer, ModulePageHeader, ModuleTablePanel, ModuleTableView,
  ModuleViewSwitch } from './project-module-table.tsx';
import type { ModuleFilterOption } from './project-module-table.tsx';
import { useModuleViewMode } from './project-module-view-mode.ts';
import { DateInput } from './date-input.tsx';

const kindLabels = { ORIGINAL: '主合同', SUPPLEMENT: '补充协议', RENEWAL: '续签合同', CORRECTION: '更正合同' } as const;
const projectStatusLabels = { PREPARING: '准备中', ACTIVE: '进行中', SUSPENDED: '已暂停', ARCHIVED: '已归档' } as const;

function contractError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const messages: Record<string, string> = {
      FORBIDDEN: '当前账号没有合同档案的操作权限。', CONTRACT_NOT_FOUND: '合同不存在或已超出当前访问范围。',
      PROJECT_NOT_FOUND: '项目不存在、已暂停，或与合同客户不一致。', REFERENCE_NOT_FOUND: '客户、上级合同或项目引用无效。',
      VERSION_CONFLICT: '合同已被其他人更新，请刷新后重试。', STATE_CONFLICT: '当前合同状态不允许执行该操作。',
      LINK_EXISTS: '该项目已经关联这份合同。', LINK_NOT_FOUND: '当前合同关系不存在或已被解除。',
      IDEMPOTENCY_PAYLOAD_CONFLICT: '本次操作内容发生变化，请重新发起。',
    };
    return messages[error.code] ?? error.message;
  }
  return error instanceof Error ? error.message : '合同操作失败，请稍后重试。';
}

function money(contract: GlobalContractSummary): string {
  return contract.amountKind === 'OPEN' ? '开放金额' : `¥ ${contract.amount ?? '0.00'}`;
}

function contractPeriod(contract: GlobalContractSummary): string {
  return contract.validFrom || contract.validUntil
    ? `${contract.validFrom ?? '—'} — ${contract.validUntil ?? '—'}` : '未限定';
}

function contractStatusClass(contract: GlobalContractSummary): string {
  return `record-status contract-${contractViewStatus(contract).toLowerCase()}`;
}

type ContractViewStatus = 'EFFECTIVE' | 'PENDING_SIGNATURE' | 'EXPIRED' | 'VOID';
function contractViewStatus(contract: GlobalContractSummary): ContractViewStatus {
  if (contract.status === 'VOID') return 'VOID';
  if (!contract.signedOn) return 'PENDING_SIGNATURE';
  if (contract.validUntil && contract.validUntil < new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Shanghai',
    year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())) return 'EXPIRED';
  return 'EFFECTIVE';
}
const contractViewLabels: Record<ContractViewStatus, string> = {
  EFFECTIVE: '生效中', PENDING_SIGNATURE: '待签署', EXPIRED: '已到期', VOID: '已作废',
};

interface ContractFormValues {
  name: string;
  customerId: string;
  kind: CreateGlobalContractRequest['kind'];
  parentContractId?: string;
  amountKind: CreateGlobalContractRequest['amountKind'];
  amount?: string;
  signedOn?: string;
  validFrom?: string;
  validUntil?: string;
  notes?: string;
  projectIds?: string[];
  reason: string;
}

function ContractDetail({ contract, canLink, onLink, onUnlink, contextProjectId }: { contract: GlobalContractSummary;
  canLink: boolean; onLink(): void; onUnlink(projectId: string): void; contextProjectId?: string }) {
  const viewStatus = contractViewStatus(contract);
  return <article className="contract-inspector prototype-contract-inspector">
    <header className="contract-inspector-head"><div><span>{contract.code} · {kindLabels[contract.kind]}</span><h2>{contract.name}</h2>
      <p>签署于 {contract.signedOn ?? '尚未签署'} · {contract.customerName}</p></div><div><i className={`record-status contract-${viewStatus.toLowerCase()}`}>{contractViewLabels[viewStatus]}</i>
      {contextProjectId ? <><Link className="prototype-button" to={`/contracts?selected=${contract.contractId}`}>查看合同主档</Link>
        {canLink && <Button danger onClick={() => onUnlink(contextProjectId)}>解除关联</Button>}</> :
        canLink && contract.status !== 'VOID' && <Button onClick={onLink}><Icon name="link" size={16} strokeWidth={2} /> 关联项目</Button>}</div></header>
    <div className="contract-inspector-body"><section className={`contract-validity contract-${viewStatus.toLowerCase()}`}><i>{viewStatus === 'EFFECTIVE' ? <Icon name="check" size={13} /> : viewStatus === 'EXPIRED' ? <Icon name="alert" size={13} /> : '◷'}</i>
      <span><small>CONTRACT STATUS</small><h3>{viewStatus === 'EFFECTIVE' ? '合同在有效履约期内' : contractViewLabels[viewStatus]}</h3>
        <p>{contract.validUntil ? `当前档案的履约到期日为 ${contract.validUntil}。` : '合同状态与项目关系分别维护，所有变更保留历史。'}</p></span><strong>{money(contract)}</strong></section>
      <section className="contract-facts-grid"><div><span>签约主体</span><strong>{contract.customerName}</strong><small>{contract.customerCode} · 客户合同主体</small></div>
        <div><span>档案类型</span><strong>{kindLabels[contract.kind]}</strong><small>{contract.parentContractId ? '已关联上级主合同' : '独立主合同档案'}</small></div>
        <div><span>履约周期</span><strong>{contract.validFrom || contract.validUntil ? `${contract.validFrom ?? '—'} — ${contract.validUntil ?? '—'}` : '未限定'}</strong><small>签署日 {contract.signedOn ?? '未登记'}</small></div>
        <div><span>服务说明</span><strong>{contract.notes || '暂无补充说明'}</strong><small>只记录合同已确认的边界</small></div></section>
      <section className="contract-files"><header><div><span>ATTACHMENTS</span><h3>附件与版本</h3><p>附件作为合同档案保存，文件更新不影响项目关系。</p></div><Tag>{contract.attachmentCount} 份</Tag></header>
        <div>{contract.attachmentCount ? <article><i><Icon name="fileText" size={14} /></i><span><small>正式附件 · 当前档案</small><b>{contract.attachmentCount} 份合同文件已经归档</b>
          <em>附件明细受独立文件权限控制</em></span><Tag color="green">已保存</Tag></article> : <div className="contract-files-empty">尚未上传合同附件</div>}</div></section>
      <section className="contract-relations"><header><div><span>PROJECT RELATIONS</span><h3>关联项目</h3><p>这里只记录合同与项目的对应关系，不设置分摊金额。</p></div>
        {!contextProjectId && canLink && contract.status !== 'VOID' && <Button onClick={onLink}><Icon name="plus" size={16} strokeWidth={2} /> 关联项目</Button>}</header><div>{contract.relatedProjects.map((project) => <div key={project.linkId}><i>{project.projectName.slice(0, 1)}</i><span>
          <small>{project.projectCode}</small><strong>{project.projectName}</strong><em>{project.linkedByName} · {project.linkedAt.replace('T',' ').slice(0,16)}</em></span>
          <Tag>{projectStatusLabels[project.projectStatus]}</Tag><Link to={`/projects/${project.projectId}/contracts`}>进入项目 <Icon name="arrowRight" size={14} /></Link>
          {canLink && contract.status !== 'VOID' && (!contextProjectId || project.projectId === contextProjectId) &&
            <button onClick={() => onUnlink(project.projectId)}>解除</button>}</div>)}
          {!contract.relatedProjects.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="这份合同暂未关联项目" />}</div></section>
      <section className="contract-archive-trail"><header><span><small>ARCHIVE TRAIL</small><h3>档案更新记录</h3></span><small>所有历史只追加不覆盖</small></header>
        <div><i /><span><b>合同档案已建立</b><small>{contract.createdAt.replace('T',' ').slice(0,16)}</small></span></div>
        {contract.updatedAt !== contract.createdAt && <div><i /><span><b>合同档案或项目关系已更新</b><small>{contract.updatedAt.replace('T',' ').slice(0,16)}</small></span></div>}</section></div>
  </article>;
}

export function GlobalContractsPage() {
  const { state } = useAuth();
  const [params, setParams] = useSearchParams();
  const token = state.kind === 'ready' ? state.token : '';
  const accountId = state.kind === 'ready' ? state.session.userId : null;
  const capabilities = new Set(state.kind === 'ready' ? state.session.capabilities : []);
  const canCreate = capabilities.has('contract.manage');
  const canLink = capabilities.has('contract.link');
  const client = useQueryClient();
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | ContractViewStatus>('ALL');
  const [selectedId, setSelectedId] = useState<string | null>(params.get('selected'));
  const [createOpen, setCreateOpen] = useState(params.get('new') === '1');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkProjectId, setLinkProjectId] = useState<string>();
  const [relationReason, setRelationReason] = useState('');
  const [unlinkProjectId, setUnlinkProjectId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [form] = Form.useForm<ContractFormValues>();
  const watchedCustomer = Form.useWatch('customerId', form);
  const watchedKind = Form.useWatch('kind', form);
  const watchedAmount = Form.useWatch('amountKind', form);
  const contracts = useInfiniteQuery({ queryKey: ['global-contracts', accountId, activeSearch],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listGlobalContracts(token, activeSearch, pageParam ?? undefined),
    getNextPageParam: (page) => page.nextCursor ?? undefined, enabled: !!token && !!accountId });
  const customers = useQuery({ queryKey: ['customers', accountId, 'contract-options'],
    queryFn: () => listCustomers(token), enabled: createOpen && !!token });
  const projects = useInfiniteQuery({ queryKey: ['contract-project-options', accountId], initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listProjects(token, '', pageParam ?? undefined),
    getNextPageParam: (page) => page.nextCursor ?? undefined, enabled: (createOpen || linkOpen) && !!token });
  const createMutation = useMutation({ mutationFn: createGlobalContract });
  const linkMutation = useMutation({ mutationFn: linkContractProject });
  const unlinkMutation = useMutation({ mutationFn: unlinkContractProject });
  const items = contracts.data?.pages.flatMap((page) => page.items) ?? [];
  const visibleItems = items.filter((item) => statusFilter === 'ALL' || contractViewStatus(item) === statusFilter);
  const selected = visibleItems.find((item) => item.contractId === selectedId) ??
    items.find((item) => item.contractId === selectedId) ?? visibleItems[0];
  const projectItems = projects.data?.pages.flatMap((page) => page.items) ?? [];
  const eligibleForCustomer = projectItems.filter((project) => !watchedCustomer || project.businessEntity.id === watchedCustomer);
  const eligibleForSelected = projectItems.filter((project) => project.businessEntity.id === selected?.customerId &&
    !selected.relatedProjects.some((linked) => linked.projectId === project.projectId) && project.status !== 'SUSPENDED' &&
    project.status !== 'ARCHIVED');

  useEffect(() => {
    if (!createOpen || !params.get('project') || !projectItems.length) return;
    const requested = projectItems.find((project) => project.projectId === params.get('project'));
    if (requested) form.setFieldsValue({ customerId: requested.businessEntity.id, projectIds: [requested.projectId] });
  }, [createOpen, form, params, projectItems]);

  async function create(values: ContractFormValues) {
    setNotice(null);
    const draft: CreateGlobalContractRequest = { expectedVersion: 0, name: values.name.trim(), customerId: values.customerId,
      kind: values.kind, parentContractId: values.kind === 'ORIGINAL' ? null : values.parentContractId ?? null,
      amountKind: values.amountKind, amount: values.amountKind === 'OPEN' ? null : values.amount?.trim() ?? null,
      signedOn: values.signedOn || null, validFrom: values.validFrom || null, validUntil: values.validUntil || null,
      notes: values.notes?.trim() || null, projectIds: values.projectIds ?? [], reason: values.reason.trim() };
    try {
      const saved = await createMutation.mutateAsync({ token, draft, idempotencyKey: `contract-create-${crypto.randomUUID()}` });
      setCreateOpen(false); form.resetFields(); setParams({}, { replace: true }); setSelectedId(saved.contractId);
      setNotice({ type: 'success', text: `${saved.code} 已建立${saved.relatedProjects.length ? '并关联项目' : '，暂不关联项目'}。` });
      await client.invalidateQueries({ queryKey: ['global-contracts', accountId] });
    } catch (error) { setNotice({ type: 'error', text: contractError(error) }); }
  }

  async function link() {
    if (!selected || !linkProjectId || relationReason.trim().length < 2) return;
    try {
      await linkMutation.mutateAsync({ token, contractId: selected.contractId, projectId: linkProjectId,
        expectedVersion: selected.version, reason: relationReason.trim(), idempotencyKey: `contract-link-${crypto.randomUUID()}` });
      setLinkOpen(false); setLinkProjectId(undefined); setRelationReason('');
      setNotice({ type: 'success', text: '项目关系已建立。' });
      await client.invalidateQueries({ queryKey: ['global-contracts', accountId] });
    } catch (error) { setNotice({ type: 'error', text: contractError(error) }); }
  }

  async function unlink() {
    if (!selected || !unlinkProjectId || relationReason.trim().length < 2) return;
    try {
      await unlinkMutation.mutateAsync({ token, contractId: selected.contractId, projectId: unlinkProjectId,
        expectedVersion: selected.version, reason: relationReason.trim(), idempotencyKey: `contract-unlink-${crypto.randomUUID()}` });
      setUnlinkProjectId(null); setRelationReason(''); setNotice({ type: 'success', text: '项目关系已解除，合同与历史仍保留。' });
      await client.invalidateQueries({ queryKey: ['global-contracts', accountId] });
    } catch (error) { setNotice({ type: 'error', text: contractError(error) }); }
  }

  const parentOptions = items.filter((item) => item.customerId === watchedCustomer && item.kind === 'ORIGINAL' && item.status !== 'VOID');
  const linkedProjectCount = new Set(items.flatMap((item) => item.relatedProjects.map((project) => project.projectId))).size;
  const effectiveCount = items.filter((item) => contractViewStatus(item) === 'EFFECTIVE').length;
  return <main className="page-shell contracts-page prototype-contracts-page">
    <header className="operation-page-head contracts-page-head"><div className="operation-page-title"><span>CONTRACT LIBRARY</span><h1>合同档案</h1>
      <p>合同是独立档案；项目只与它建立关联，同一合同可以覆盖多个项目。</p></div><div className="operation-page-aside">
      <section className="operation-summary-strip contracts-summary-strip"><button onClick={() => setStatusFilter('ALL')}><span>合同档案</span><strong>{items.length}</strong><small>全局独立保存</small></button>
        <button className="is-dark" onClick={() => setStatusFilter('EFFECTIVE')}><span>生效中</span><strong>{effectiveCount}</strong><small>当前有效合同</small></button>
        <div><span>关联项目</span><strong>{linkedProjectCount}</strong><small>去重项目数</small></div><button className={items.some((item) => !item.relatedProjects.length) ? 'is-warning' : ''} onClick={() => setStatusFilter('ALL')}>
          <span>暂未关联</span><strong>{items.filter((item) => !item.relatedProjects.length).length}</strong><small>允许稍后关联</small></button></section>
      <div className="operation-page-actions">{canCreate && <Button type="primary" onClick={() => setCreateOpen(true)}><Icon name="plus" size={16} strokeWidth={2} /> 新建合同档案</Button>}</div></div></header>
    {notice && <Alert className="workspace-alert" type={notice.type} showIcon title={notice.text} closable onClose={() => setNotice(null)} />}
    <section className="operation-toolbar contracts-toolbar"><div>{([['ALL','全部'],['EFFECTIVE','生效中'],['PENDING_SIGNATURE','待签署'],['EXPIRED','已到期'],['VOID','已作废']] as const).map(([value,label]) =>
      <button key={value} className={statusFilter === value ? 'is-active' : ''} onClick={() => setStatusFilter(value)}>{label}</button>)}</div>
      <Input.Search allowClear value={search} onChange={(event) => setSearch(event.target.value)} onSearch={() => setActiveSearch(search.trim())}
        placeholder="搜索合同、编号或签约方" /></section>
    {contracts.isPending && <Skeleton active paragraph={{ rows: 10 }} />}
    {contracts.error && <Alert type="error" showIcon title={contractError(contracts.error)} />}
    {!contracts.isPending && !items.length ? <Empty description="当前范围内还没有合同档案" /> : <section className="contracts-workspace">
      <aside className="contract-index"><header><span><b>{visibleItems.length}</b> 份合同档案</span><small>按档案统一管理</small></header><div>{visibleItems.map((contract) =>
        <button key={contract.contractId} className={selected?.contractId === contract.contractId ? 'is-active' : ''}
          onClick={() => { setSelectedId(contract.contractId); const next = new URLSearchParams(params); next.set('selected', contract.contractId); setParams(next, { replace: true }); }}>
          <i>合</i><span><small>{contract.code} · {kindLabels[contract.kind]}</small><strong>{contract.name}</strong>
          <em>{contract.validFrom || contract.validUntil ? `${contract.validFrom ?? '—'} — ${contract.validUntil ?? '—'}` : contract.customerName}</em></span>
          <span><i className={`record-status contract-${contractViewStatus(contract).toLowerCase()}`}>{contractViewLabels[contractViewStatus(contract)]}</i>
          <small>{contract.relatedProjects.length} 个项目 · {contract.attachmentCount} 份附件</small></span></button>)}</div>
        {contracts.hasNextPage && <Button block loading={contracts.isFetchingNextPage}
          onClick={() => void contracts.fetchNextPage()}>继续加载</Button>}</aside>
      {selected && <ContractDetail contract={selected} canLink={canLink} onLink={() => { setRelationReason(''); setLinkOpen(true); }}
        onUnlink={(projectId) => { setRelationReason(''); setUnlinkProjectId(projectId); }} />}
    </section>}

    <Modal open={createOpen} onCancel={() => setCreateOpen(false)} footer={null} width={760} title="新增合同档案" destroyOnHidden>
      <Form form={form} layout="vertical" initialValues={{ kind: 'ORIGINAL', amountKind: 'OPEN', projectIds: [] }}
        onFinish={(values: ContractFormValues) => void create(values)} className="contract-form">
        <div className="contract-form-grid"><Form.Item label="合同名称" name="name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item label="客户" name="customerId" rules={[{ required: true }]}><Select showSearch optionFilterProp="label"
            loading={customers.isPending} options={customers.data?.items.map((item) => ({ value: item.customerId,
              label: `${item.name} · ${item.code}` })) ?? []} /></Form.Item>
          <Form.Item label="合同类型" name="kind" rules={[{ required: true }]}><Select options={Object.entries(kindLabels)
            .map(([value, label]) => ({ value, label }))} /></Form.Item>
          {watchedKind !== 'ORIGINAL' && <Form.Item label="上级主合同" name="parentContractId" rules={[{ required: true }]}>
            <Select options={parentOptions.map((item) => ({ value: item.contractId, label: `${item.code} · ${item.name}` }))} /></Form.Item>}
          <Form.Item label="金额方式" name="amountKind" rules={[{ required: true }]}><Select options={[
            { value: 'OPEN', label: '开放金额' }, { value: 'FIXED', label: '固定金额' }]} /></Form.Item>
          {watchedAmount === 'FIXED' && <Form.Item label="合同金额" name="amount" rules={[{ required: true,
            pattern: /^(?:0|[1-9]\d{0,15})(?:\.\d{1,2})?$/ }]}><Input prefix="¥" /></Form.Item>}
          <Form.Item label="签署日期" name="signedOn"><DateInput /></Form.Item>
          <Form.Item label="有效期开始" name="validFrom"><DateInput /></Form.Item>
          <Form.Item label="有效期结束" name="validUntil"><DateInput /></Form.Item>
          <Form.Item label="关联项目（可留空）" name="projectIds" className="is-wide"><Select mode="multiple"
            placeholder="可先只建立合同档案" options={eligibleForCustomer.map((project) => ({ value: project.projectId,
              label: `${project.projectCode} · ${project.name}` }))} /></Form.Item>
          <Form.Item label="合同说明" name="notes" className="is-wide"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item label="建档原因" name="reason" className="is-wide" rules={[{ required: true, min: 2 }]}>
            <Input.TextArea rows={2} placeholder="用于审计记录，不会显示在合同标题中" /></Form.Item></div>
        <div className="contract-form-note">此处先建立合同主档与项目关系；项目关系不代表金额分摊。</div>
        <div className="contract-form-actions"><Button onClick={() => setCreateOpen(false)}>取消</Button>
          <Button type="primary" htmlType="submit" loading={createMutation.isPending}>建立合同档案</Button></div>
      </Form>
    </Modal>

    <Modal open={linkOpen} title={`关联项目 · ${selected?.code ?? ''}`} onCancel={() => setLinkOpen(false)}
      onOk={() => void link()} confirmLoading={linkMutation.isPending}
      okButtonProps={{ disabled: !linkProjectId || relationReason.trim().length < 2 }}>
      <div className="contract-relation-form"><label><span>选择同客户项目</span><Select value={linkProjectId} onChange={setLinkProjectId}
        options={eligibleForSelected.map((project) => ({ value: project.projectId, label: `${project.projectCode} · ${project.name}` }))}
        placeholder="选择项目" /></label><label><span>关联原因</span><Input.TextArea rows={3} value={relationReason}
        onChange={(event) => setRelationReason(event.target.value)} /></label></div>
    </Modal>
    <Modal open={!!unlinkProjectId} title="解除合同与项目的关系" onCancel={() => setUnlinkProjectId(null)}
      onOk={() => void unlink()} confirmLoading={unlinkMutation.isPending} okText="确认解除"
      okButtonProps={{ danger: true, disabled: relationReason.trim().length < 2 }}>
      <p>合同主档、附件与历史不会删除；项目内将不再显示这份合同。</p>
      <Input.TextArea rows={3} value={relationReason} onChange={(event) => setRelationReason(event.target.value)}
        placeholder="填写解除原因" />
    </Modal>
  </main>;
}

export function ProjectRelatedContractsPage() {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const { state } = useAuth();
  const [params, setParams] = useSearchParams();
  const [viewMode, setViewMode] = useModuleViewMode(params, setParams);
  const token = state.kind === 'ready' ? state.token : '';
  const accountId = state.kind === 'ready' ? state.session.userId : null;
  const canLink = state.kind === 'ready' && state.session.capabilities.includes('contract.link');
  const client = useQueryClient();
  const project = useQuery({ queryKey: ['project', accountId, projectId], queryFn: () => getProject(token, projectId),
    enabled: !!token && !!projectId });
  const contracts = useInfiniteQuery({ queryKey: ['global-contracts', accountId, 'project-relations'],
    initialPageParam: null as string | null, queryFn: ({ pageParam }) => listGlobalContracts(token, '', pageParam ?? undefined),
    getNextPageParam: (page) => page.nextCursor ?? undefined, enabled: !!token && !!projectId });
  const [linkTarget, setLinkTarget] = useState<string>();
  const [unlinkTarget, setUnlinkTarget] = useState<GlobalContractSummary | null>(null);
  const [reason, setReason] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | ContractViewStatus>('ALL');
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const linkMutation = useMutation({ mutationFn: linkContractProject });
  const unlinkMutation = useMutation({ mutationFn: unlinkContractProject });
  const all = contracts.data?.pages.flatMap((page) => page.items) ?? [];
  const linked = all.filter((contract) => contract.relatedProjects.some((relation) => relation.projectId === projectId));
  const normalized = search.trim().toLowerCase();
  const matched = linked.filter((contract) => !normalized ||
    `${contract.code} ${contract.name} ${contract.customerName}`.toLowerCase().includes(normalized));
  const visible = matched.filter((contract) => statusFilter === 'ALL' || contractViewStatus(contract) === statusFilter);
  const countStatus = (status: ContractViewStatus) =>
    matched.filter((contract) => contractViewStatus(contract) === status).length;
  const statusFilters: ModuleFilterOption[] = [
    { value: 'ALL', label: '全部', count: matched.length },
    { value: 'EFFECTIVE', label: '生效中', count: countStatus('EFFECTIVE') },
    { value: 'PENDING_SIGNATURE', label: '待签署', count: countStatus('PENDING_SIGNATURE') },
    { value: 'EXPIRED', label: '已到期', count: countStatus('EXPIRED') },
    { value: 'VOID', label: '已作废', count: countStatus('VOID') },
  ];
  const requestedId = params.get('contract');
  const selected = visible.find((contract) => contract.contractId === requestedId) ??
    linked.find((contract) => contract.contractId === requestedId) ?? visible[0];
  const candidates = all.filter((contract) => contract.customerId === project.data?.businessEntity.id && contract.status !== 'VOID' &&
    !contract.relatedProjects.some((relation) => relation.projectId === projectId));
  const selectedCandidate = candidates.find((contract) => contract.contractId === linkTarget);
  const effectiveCount = linked.filter((contract) => contractViewStatus(contract) === 'EFFECTIVE').length;
  const attachmentCount = linked.reduce((sum, contract) => sum + contract.attachmentCount, 0);
  const pendingSignatureCount = linked.filter((contract) => contractViewStatus(contract) === 'PENDING_SIGNATURE').length;
  const tableDetail = requestedId ? linked.find((contract) => contract.contractId === requestedId) ?? null : null;

  function chooseContract(contractId: string) {
    const next = new URLSearchParams(params); next.set('contract', contractId); setParams(next, { replace: true });
  }

  function closeDetail() {
    const next = new URLSearchParams(params); next.delete('contract'); setParams(next, { replace: true });
  }

  function openLink() {
    setReason('');
    setLinkTarget(candidates[0]?.contractId);
  }

  async function link() {
    if (!selectedCandidate || reason.trim().length < 2) return;
    try {
      await linkMutation.mutateAsync({ token, contractId: selectedCandidate.contractId, projectId,
        expectedVersion: selectedCandidate.version, reason: reason.trim(), idempotencyKey: `contract-link-${crypto.randomUUID()}` });
      setLinkTarget(undefined); setReason(''); setNotice({ type: 'success', text: `${selectedCandidate.code} 已关联到当前项目。` });
      await Promise.all([client.invalidateQueries({ queryKey: ['global-contracts', accountId] }),
        client.invalidateQueries({ queryKey: ['project', accountId, projectId] })]);
    } catch (error) { setNotice({ type: 'error', text: contractError(error) }); }
  }

  async function unlink() {
    if (!unlinkTarget || reason.trim().length < 2) return;
    try {
      await unlinkMutation.mutateAsync({ token, contractId: unlinkTarget.contractId, projectId,
        expectedVersion: unlinkTarget.version, reason: reason.trim(), idempotencyKey: `contract-unlink-${crypto.randomUUID()}` });
      setUnlinkTarget(null); closeDetail(); setReason(''); setNotice({ type: 'success', text: '项目关系已解除，合同档案继续保留。' });
      await Promise.all([client.invalidateQueries({ queryKey: ['global-contracts', accountId] }),
        client.invalidateQueries({ queryKey: ['project', accountId, projectId] })]);
    } catch (error) { setNotice({ type: 'error', text: contractError(error) }); }
  }

  function linkedBy(contract: GlobalContractSummary): string {
    return contract.relatedProjects.find((relation) => relation.projectId === projectId)?.linkedByName ?? '—';
  }

  function renderInspector(selected: GlobalContractSummary | null) {
    if (!selected) return null;
    return <ContractDetail contract={selected} canLink={canLink} contextProjectId={projectId} onLink={openLink}
      onUnlink={() => { setReason(''); setUnlinkTarget(selected); }} />;
  }

  const contractColumns: TableColumnsType<GlobalContractSummary> = [
    { title: '合同编号', dataIndex: 'code', key: 'code', width: 132,
      render: (code: string) => <span className="content-table-no">{code}</span> },
    { title: '合同名称', dataIndex: 'name', key: 'name', width: 300,
      render: (name: string) => <b title={name}>{name}</b> },
    { title: '客户', dataIndex: 'customerName', key: 'customer', width: 220,
      render: (customerName: string) => <span className="content-table-muted" title={customerName}>{customerName}</span> },
    { title: '有效期', key: 'period', width: 200,
      render: (_value, contract) => <span className="content-table-no">{contractPeriod(contract)}</span> },
    { title: '状态', key: 'status', width: 108,
      render: (_value, contract) => <i className={contractStatusClass(contract)}>{contractViewLabels[contractViewStatus(contract)]}</i> },
    { title: '关联金额', key: 'amount', width: 140, align: 'right',
      render: (_value, contract) => <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{money(contract)}</strong> },
    { title: '关联人', key: 'linkedBy', width: 110,
      render: (_value, contract) => <span className="content-table-person" title={linkedBy(contract)}>{linkedBy(contract)}</span> },
  ];

  return <main className={`page-shell contracts-page prototype-contracts-page project-contract-relations${viewMode === 'table' ? ' is-table-mode' : ''}`}>
    <header className="operation-page-head contracts-page-head"><div className="operation-page-title"><span>PROJECT RELATIONS</span><h1>关联合同</h1>
      <p>{project.data?.name ?? '当前项目'} · 这里只维护项目与合同的关联，不分摊合同金额。</p></div>
      <div className="operation-page-aside"><section className="operation-summary-strip contracts-summary-strip">
        <button onClick={() => setStatusFilter('ALL')}><span>已关联</span><strong>{linked.length}</strong><small>合同档案</small></button>
        <button className="is-dark" onClick={() => setStatusFilter('EFFECTIVE')}><span>生效中</span><strong>{effectiveCount}</strong><small>当前有效合同</small></button>
        <div><span>附件版本</span><strong>{attachmentCount}</strong><small>随合同统一保存</small></div>
        <button className={pendingSignatureCount ? 'is-warning' : ''} onClick={() => setStatusFilter('PENDING_SIGNATURE')}>
          <span>待签署</span><strong>{pendingSignatureCount}</strong><small>需要后续补齐</small></button></section>
        {canLink && <div className="operation-page-actions"><Button disabled={!candidates.length} onClick={openLink}><Icon name="link" size={16} strokeWidth={2} /> 关联已有合同</Button>
          <Link to={`/contracts?new=1&project=${projectId}`}><Button type="primary"><Icon name="plus" size={16} strokeWidth={2} /> 新建并关联</Button></Link></div>}</div></header>
    {viewMode === 'table' && <ModulePageHeader projectId={projectId} kicker={`项目数据 · ${project.data?.name ?? '当前项目'} · 关联合同`}
      title="关联合同" copy={`${project.data?.name ?? '当前项目'} · 这里只维护项目与合同的关联，不分摊合同金额。`}
      stats={[
        { label: '已关联', value: String(linked.length), note: '合同档案' },
        { label: '生效中', value: String(effectiveCount), note: '当前有效合同', tone: 'dark' },
        { label: '附件版本', value: String(attachmentCount), note: '随合同统一保存' },
        { label: '待签署', value: String(pendingSignatureCount), note: '需要后续补齐',
          ...(pendingSignatureCount ? { tone: 'danger' as const } : {}) },
      ]} />}
    {notice && <Alert className="workspace-alert" type={notice.type} showIcon title={notice.text} closable onClose={() => setNotice(null)} />}
    {viewMode === 'split' && (project.isPending || contracts.isPending) && <Skeleton active paragraph={{ rows: 8 }} />}
    {(project.error || contracts.error) && <Alert type="error" showIcon title={contractError(project.error ?? contracts.error)} />}
    {viewMode === 'split' && <>
      <section className="operation-toolbar contracts-toolbar"><div>{([['ALL','全部'],['EFFECTIVE','生效中'],['PENDING_SIGNATURE','待签署'],['EXPIRED','已到期'],['VOID','已作废']] as const).map(([value,label]) =>
        <button key={value} className={statusFilter === value ? 'is-active' : ''} onClick={() => setStatusFilter(value)}>{label}</button>)}</div>
        <Input.Search allowClear value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索合同、编号或签约方" />
        <ModuleViewSwitch value={viewMode} onChange={setViewMode} /></section>
      {!contracts.isPending && !linked.length ? <section className="project-contract-empty"><span>⌁</span><h2>当前项目还没有关联合同</h2>
        <p>项目可以在没有合同的情况下继续准备；需要时再关联已有合同或建立新合同。</p>
        {canLink && <div><Button disabled={!candidates.length} onClick={openLink}>关联已有合同</Button>
          <Link to={`/contracts?new=1&project=${projectId}`}><Button type="primary">新建并关联</Button></Link></div>}</section> :
        <section className="contracts-workspace"><aside className="contract-index"><header><span><b>{visible.length}</b> 份关联合同</span><small>同一合同可关联多个项目</small></header><div>{visible.map((contract) =>
          <button key={contract.contractId} className={selected?.contractId === contract.contractId ? 'is-active' : ''}
            onClick={() => chooseContract(contract.contractId)}>
            <i>合</i><span><small>{contract.code} · {kindLabels[contract.kind]}</small><strong>{contract.name}</strong>
            <em>{contract.validFrom || contract.validUntil ? `${contract.validFrom ?? '—'} — ${contract.validUntil ?? '—'}` : contract.customerName}</em></span>
            <span><i className={`record-status contract-${contractViewStatus(contract).toLowerCase()}`}>{contractViewLabels[contractViewStatus(contract)]}</i>
            <small>{contract.relatedProjects.length} 个项目 · {contract.attachmentCount} 份附件</small></span></button>)}</div></aside>
          {renderInspector(selected ?? null)}</section>}
    </>}
    {viewMode === 'table' && <ModuleTablePanel title="关联合同" count={visible.length} filters={statusFilters}
      filterValue={statusFilter} onFilterChange={(value) => setStatusFilter(value as 'ALL' | ContractViewStatus)}
      searchValue={search} onSearchChange={setSearch} searchPlaceholder="搜索合同名称、编号或客户"
      viewMode={viewMode} onViewChange={setViewMode}
      action={canLink ? <><Button disabled={!candidates.length} onClick={openLink}><Icon name="link" size={16} strokeWidth={2} /> 关联已有合同</Button>
        <Link to={`/contracts?new=1&project=${projectId}`}><Button type="primary"><Icon name="plus" size={16} strokeWidth={2} /> 新建并关联</Button></Link></> : null}>
      <ModuleTableView rows={visible} columns={contractColumns} rowKey={(row) => row.contractId}
        onOpen={(row) => chooseContract(row.contractId)} focusedKey={tableDetail?.contractId ?? null}
        loading={contracts.isPending} minWidth={1320}
        emptyText={linked.length ? '没有符合当前筛选的记录' : '当前项目还没有关联合同'} />
    </ModuleTablePanel>}

    <Modal open={!!linkTarget} title="关联已有合同" onCancel={() => setLinkTarget(undefined)} onOk={() => void link()}
      confirmLoading={linkMutation.isPending} okButtonProps={{ disabled: !linkTarget || reason.trim().length < 2 }}>
      <div className="contract-relation-form"><label><span>合同档案</span><Select value={linkTarget} onChange={setLinkTarget}
        options={candidates.map((contract) => ({ value: contract.contractId, label: `${contract.code} · ${contract.name}` }))} /></label>
        <label><span>关联原因</span><Input.TextArea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} /></label></div>
    </Modal>
    <Modal open={!!unlinkTarget} title="解除当前项目关系" onCancel={() => setUnlinkTarget(null)} onOk={() => void unlink()}
      okText="确认解除" confirmLoading={unlinkMutation.isPending} zIndex={1200}
      okButtonProps={{ danger: true, disabled: reason.trim().length < 2 }}><p>合同主档、附件和历史不会删除。</p>
      <Input.TextArea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="填写解除原因" /></Modal>
    <ModuleFullDetailDrawer open={!!tableDetail} onClose={closeDetail}>
      {renderInspector(tableDetail)}
    </ModuleFullDetailDrawer>
  </main>;
}
